import useStore from 'state/knovStore'

export const AI_ACTION_FROM_MEDIA_EVENT = 'knov:insert-ai-action-from-media'

const panelList = () => {
    const panels = useStore.getState().panels
    return panels.getPanels?.() || panels.state || []
}

const panelForQuest = questId => {
    if (!questId) return null
    const normalizedQuestId = String(questId)
    return panelList().find(
        panel => !panel?.empty && String(panel?.filter?.questId || '') === normalizedQuestId,
    )
}

const panelById = panelId => {
    if (!panelId) return null
    return useStore.getState().panels.getPanel?.(panelId) || null
}

const hasMountedNewAnswer = panel =>
    Boolean(panel?.getScrollContainer?.()?.querySelector?.('.new-answer-comp'))

const dispatchMediaAiAction = detail => {
    window.dispatchEvent(new CustomEvent(AI_ACTION_FROM_MEDIA_EVENT, { detail }))
}

const isQuestStreamPanel = panel => {
    const filter = panel?.filter || {}
    return Boolean(
        panel &&
            !panel.empty &&
            !filter.questId &&
            !filter.streams &&
            !filter.ordinals &&
            !filter.user_ordinals,
    )
}

const destinationQuestIdForMediaAction = ({ detail, sourcePanel }) => {
    const openThreadQuestId = sourcePanel?.filter?.questId
    if (openThreadQuestId) return openThreadQuestId
    if (isQuestStreamPanel(sourcePanel)) return detail?.childQuestId || detail?.questId || null
    return detail?.questId || null
}

const waitForThreadPanel = ({ questId, panelId, timeoutMs = 5000 }) =>
    new Promise(resolve => {
        const startedAt = Date.now()

        const poll = () => {
            const panel = panelById(panelId) || panelForQuest(questId)
            if (panel && hasMountedNewAnswer(panel)) {
                resolve(panel)
                return
            }

            if (Date.now() - startedAt >= timeoutMs) {
                resolve(panel || null)
                return
            }

            setTimeout(poll, 100)
        }

        poll()
    })

const scrollPanelToBottom = panel =>
    new Promise(resolve => {
        if (!panel?.scrollToBottom) {
            resolve()
            return
        }

        let resolved = false
        const finish = () => {
            if (resolved) return
            resolved = true
            resolve()
        }

        panel.scrollToBottom(finish)
        setTimeout(finish, 1000)
    })

const waitForOpenRightPanelReady = async ({ openRight, questId }) => {
    let panelReadyResolve = null
    const panelReady = new Promise(resolve => {
        panelReadyResolve = resolve
    })

    const result = await openRight?.(
        { questId },
        {
            suppressAutoReveal: true,
            onPanelReady: panelId => panelReadyResolve(panelId),
        },
    )
    const fallbackPanelId = result?.panelId || null
    const readyPanelId = await Promise.race([
        panelReady,
        new Promise(resolve => setTimeout(() => resolve(fallbackPanelId), 1200)),
    ])

    return readyPanelId || fallbackPanelId
}

export const dispatchMediaAiActionWhenThreadReady = async ({
    detail,
    openRight,
    sourcePanel = null,
}) => {
    const questId = destinationQuestIdForMediaAction({ detail, sourcePanel })
    const dispatchDetail = questId ? { ...detail, questId } : detail
    const existingThreadPanel = panelForQuest(questId)
    if (
        !questId ||
        existingThreadPanel ||
        !isQuestStreamPanel(sourcePanel) ||
        typeof openRight !== 'function'
    ) {
        await scrollPanelToBottom(existingThreadPanel || sourcePanel)
        dispatchMediaAiAction(dispatchDetail)
        return
    }

    let targetPanelId = null
    try {
        targetPanelId = await waitForOpenRightPanelReady({ openRight, questId })
    } catch (error) {
        console.error('Failed to open thread for media AI action', error)
    }

    const targetPanel = await waitForThreadPanel({ questId, panelId: targetPanelId })
    await scrollPanelToBottom(targetPanel)
    dispatchMediaAiAction(dispatchDetail)
}
